import { SdkContext } from '../internal'
import { noteRowFrom, versionedRowWrite } from '@/lib/realtime/rowVersion'
import type { EmailPreferences } from './email-notices'

/**
 * F-142 phase 1: the community's SMS channel (owner/admin), the member's
 * phone code, "Avisos desta comunidade" per channel and category, the
 * contact consents and the versioned consent texts of signup and login.
 * The community is always the `X-CommunityId`.
 */

export interface SmsUsage {
  messages: number
  parts: number
}

/** F-34: a plan quota for "X de Y": the month, or the annual plan's term, where the monthly quota accumulates. */
export interface QuotaMeter {
  used: number
  /** The plan's **monthly** value: a number or `unlimited`. */
  limit: number | 'unlimited'
  period: 'month' | 'annual-term'
  /** What is left in the period (with the accumulated part on annual); null when unlimited. */
  available: number | null
  resetsAt: string
  percent: number | null
}

export interface SmsSettings {
  /** The period's quota; newer than `monthlyQuota`, which stays for older readers. */
  quota?: QuotaMeter
  enabled: boolean
  dailyCap: number | null
  dailyCapMax: number
  /** The plan's monthly quota; `null` = no limit. */
  monthlyQuota: number | null
  usage: { today: SmsUsage; month: SmsUsage }
  /** `*` = every country (global sending) except `blockedCountries`. */
  countries: string[]
  blockedCountries?: string[]
  quietHours: { start: number; end: number }
  timeZone: string
  purposes: string[]
  /** Workflow messages per person per day, counted in each channel (SMS and WhatsApp). */
  personDailyCap?: number
  personDailyCapRange?: { min: number; max: number }
}

export interface SmsSendResult {
  /** `sent` right away; `skipped` with `reason`; `failed` with the provider's `code`. `queued` is an older API. */
  status: 'sent' | 'skipped' | 'failed' | 'queued'
  code?: string | null
  reason?: string | null
  messageId?: string
  notBefore?: string
  deferred?: boolean
  parts?: number
  encoding?: 'GSM' | 'UCS2'
}

export interface SmsNoticeCategory {
  key: string
  label: string
  enabled: boolean
  defaultEnabled?: boolean
}

export interface SmsNoticePreference {
  /** F-142 phase 2: the WhatsApp block has the same shape (`consent.marketing` always false for now). */
  channel: 'sms' | 'whatsapp'
  /** Whether the community turned the channel on. */
  available: boolean
  /** The account's phone, masked. */
  phone: string | null
  phoneVerified: boolean
  countrySupported: boolean | null
  /** False after the opt-out link or turning it off here. */
  enabled: boolean
  optedOutAt: string | null
  /** API-191: the person replied SAIR (or STOP) to the shared Memberfy number, so the stop covers every community. Absent on older APIs. */
  globalOptOut?: GlobalOptOut | null
  consent: { service: boolean; marketing: boolean; needsPrompt: boolean }
  categories: SmsNoticeCategory[]
  /** WhatsApp only: the platform messages the community left on, with the person's choice (or the default). */
  moments?: Array<{ key: string; label: string; category: string; defaultOn: boolean; enabled: boolean; needsMarketingConsent?: boolean }>
}

export interface GlobalOptOut {
  optedOutAt: string
  /** The word the person sent (or the carrier's code). */
  keyword: string | null
  allCommunities: boolean
  /** False when the carrier blocked the number (SMS STOP): only replying `resumeKeyword` lifts it, and the PUT answers 409. */
  resumableFromScreen: boolean
  resumeKeyword: string | null
  /** The API's own notice, in the request's language. */
  notice?: string
}

export interface NoticePreferences {
  channels: { email: EmailPreferences; sms: SmsNoticePreference; whatsapp?: SmsNoticePreference }
}

export interface NoticePreferencesUpdate {
  /** `textVersion`: the combined text's version (`texts.<channel>.all`), recording service and news together. */
  sms?: { enabled?: boolean; categories?: Record<string, boolean>; marketing?: boolean; textVersion?: string }
  whatsapp?: { enabled?: boolean; categories?: Record<string, boolean>; moments?: Record<string, boolean>; marketing?: boolean; textVersion?: string }
}

/** `team`: consent the community's team declared it collected (with `attestationMethod`). */
export type ConsentOrigin = 'signup' | 'login' | 'import' | 'profile' | 'team'

export interface ContactConsent {
  id: string
  channel: 'sms' | 'whatsapp'
  purpose: 'service' | 'marketing'
  textVersion: string
  current: boolean
  origin: ConsentOrigin
  consentedAt: string
  attestedAt: string | null
  /** Free text: how the community collected it (set on `team` and attested imports). */
  attestationMethod?: string | null
  revokedAt: string | null
}

export interface ConsentText {
  version: string
  text: string
}

export interface ConsentTexts {
  /** `all`: the channel's combined notices-and-news text (`<channel>-all:<date>`), the one the profile toggle shows and sends. */
  sms: { service: ConsentText; marketing: ConsentText; all?: ConsentText }
  /** F-142 phase 2: WhatsApp has its own texts (they name WhatsApp and the community). */
  whatsapp?: { service: ConsentText; marketing: ConsentText; all?: ConsentText }
  /** One service text for SMS and WhatsApp together (version `combined:<date>`); `all`: notices and news for both (`combined-all:<date>`). */
  combined?: { service: ConsentText; all?: ConsentText }
  /** The channels this community offers. */
  offered?: { sms: boolean; whatsapp: boolean }
  /** The one text signup and login show (combined.all, sms.all or whatsapp.all), `null` with no channel offered. */
  signup?: ConsentText | null
}

/** What signup and login send: the purposes the person agreed to. */
export interface SmsConsentChoice {
  service?: boolean
  marketing?: boolean
  /** F-142 phase 2: the WhatsApp texts, recorded on the `whatsapp` channel. */
  whatsapp?: { service?: boolean; marketing?: boolean }
  /** The combined text's version, when that is the text shown: each channel's service consent is stored under it. */
  textVersion?: string
}

export interface PhoneCodeRequest {
  phone: string
  expiresAt?: string
  resendAfterSeconds: number
  /** Where the code went; absent means SMS (older API). WhatsApp when the community's SMS is off. */
  sentVia?: 'sms' | 'whatsapp'
  channel?: 'sms' | 'whatsapp'
}

export interface SmsSegmentCount {
  encoding: 'GSM' | 'UCS2'
  units: number
  parts: number
  length?: number
}

export class SmsResource {
  constructor(private ctx: SdkContext) {}

  /** Owner/admin. */
  async getSettings(): Promise<SmsSettings> {
    return noteRowFrom('messagingSettings', await this.ctx.call<SmsSettings>({ httpMethod: 'GET', path: '/sms/settings' }))
  }

  /** Owner/admin. Turning it on needs `dailyCap` (now or already saved): 422 on `dailyCap` otherwise. */
  async updateSettings(body: { enabled?: boolean; dailyCap?: number | null; personDailyCap?: number }): Promise<SmsSettings> {
    /* One row with WhatsApp: one version for both cards. */
    return versionedRowWrite('messagingSettings', (v) => this.ctx.call<SmsSettings>({ httpMethod: 'PUT', path: '/sms/settings', hasBody: true }, { body: { ...body, ...(v ? { updatedAt: v } : {}) } }))
  }

  /** Owner/admin: a service notice to the caller's own confirmed phone, with every rule. */
  async sendTest(): Promise<SmsSendResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/sms/test', hasBody: true }, { body: {} })
  }

  /** Team: the parts of a text alone and as it goes out (`[Community] ` in front, the opt-out line at the end). Sends nothing. */
  async segments(text: string): Promise<{ text: SmsSegmentCount; sent: SmsSegmentCount }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/sms/segments', hasBody: true }, { body: { text } })
  }

  /** Any role: sends the 6-digit code to `phone` (or the account's phone). */
  async requestPhoneCode(body: { phone?: string; phoneCountry?: string }): Promise<PhoneCodeRequest> {
    return this.ctx.call({ httpMethod: 'POST', path: '/members/me/phone-verification', hasBody: true }, { body })
  }

  async confirmPhoneCode(code: string): Promise<{ phone: string; phoneVerified: boolean; phoneVerifiedAt?: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/members/me/phone-verification/confirm', hasBody: true }, { body: { code } })
  }

  async getNoticePreferences(): Promise<NoticePreferences> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/me/notice-preferences' })
  }

  /** The preferences, with the response's `message` (API-191: after lifting a SAIR it says the channel is back in every community). */
  async updateNoticePreferences(body: NoticePreferencesUpdate): Promise<NoticePreferences & { message?: string }> {
    const res = await this.ctx.envelope<{ data: NoticePreferences; message?: string }>({ httpMethod: 'PUT', path: '/members/me/notice-preferences', hasBody: true }, { body })
    return { ...res.data, message: res.message }
  }

  async myConsents(): Promise<ContactConsent[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/me/contact-consents' })
  }

  /** Owner/admin: a member's consents, never the IP. */
  async memberConsents(memberId: string): Promise<ContactConsent[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/{memberId}/contact-consents' }, { memberId })
  }

  /** Public: the current consent texts, with the community's name in them. */
  async consentTexts(): Promise<ConsentTexts> {
    return this.ctx.call({ httpMethod: 'GET', path: '/contact-consent/texts' })
  }
}
